import React, { useState, useEffect } from 'react';
import {
  FileText, Download, CheckCircle2, XCircle, AlertCircle, Clock,
  ShieldCheck, Check, X, Loader
} from 'lucide-react';
import { NonAttorneyDisclaimer } from './legal/Disclosures';

interface PublicQuoteViewProps {
  token: string;
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  Pending: { label: 'Pending Review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  Sent: { label: 'Pending Review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  Accepted: { label: 'Accepted', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  Rejected: { label: 'Declined', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export default function PublicQuoteView({ token }: PublicQuoteViewProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quoteData, setQuoteData] = useState<any | null>(null);
  const [branding, setBranding] = useState<any | null>(null);

  const [decisionNotes, setDecisionNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedStatus, setSubmittedStatus] = useState<string | null>(null);
  const [confirmDecision, setConfirmDecision] = useState<'Accepted' | 'Rejected' | null>(null);
  const [feedbackMsg, setFeedbackMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const fetchQuote = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/public/quote-link/${token}`);
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || 'Quote link is invalid or no longer active.');
          return;
        }
        setQuoteData(data.quote);
        setBranding(data.branding);
        if (data.quote?.status === 'Accepted' || data.quote?.status === 'Rejected') {
          setSubmittedStatus(data.quote.status);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load quote details. Please refresh and try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [token]);

  const handleDecision = async (decision: 'Accepted' | 'Rejected') => {
    setSubmitting(true);
    setFeedbackMsg(null);
    try {
      const res = await fetch(`/api/public/quote-link/${token}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, notes: decisionNotes }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFeedbackMsg({ type: 'error', text: data.error || 'Failed to record decision.' });
        return;
      }
      setSubmittedStatus(decision);
      setFeedbackMsg({
        type: 'success',
        text: `Thank you! You have successfully ${decision === 'Accepted' ? 'accepted' : 'declined'} this quote.`,
      });
    } catch (err: any) {
      setFeedbackMsg({ type: 'error', text: err.message || 'Network error occurred.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPdf = () => {
    window.open(`/api/public/quote-link/${token}/pdf`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 text-center space-y-3 max-w-sm w-full">
          <Loader className="h-8 w-8 text-blue-600 animate-spin mx-auto" />
          <h3 className="text-sm font-extrabold text-slate-800">Loading Official Quote Details...</h3>
          <p className="text-xs text-slate-400">Verifying secure quote link...</p>
        </div>
      </div>
    );
  }

  if (error || !quoteData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-rose-200 text-center space-y-4 max-w-md w-full">
          <div className="h-12 w-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h3 className="text-base font-extrabold text-slate-900">Quote Unavailable</h3>
          <p className="text-xs text-slate-600 leading-relaxed">{error || 'This quote link is invalid, has expired, or was cancelled.'}</p>
        </div>
      </div>
    );
  }

  const currentStatus = submittedStatus || quoteData.status;
  const badge = STATUS_BADGE[currentStatus] || STATUS_BADGE.Pending;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 text-slate-800 font-sans">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Workspace Header */}
        <div className="flex items-center justify-between bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs gap-4">
          <div className="flex items-center gap-4">
            {branding?.invoiceLogoUrl ? (
              <img src={branding.invoiceLogoUrl} alt={branding?.workspaceName || 'Logo'} className="h-10 object-contain" />
            ) : (
              <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center text-white font-extrabold text-lg">
                {(branding?.workspaceName || 'P').charAt(0)}
              </div>
            )}
            <div>
              <h2 className="text-base font-extrabold text-slate-900">{branding?.workspaceName || 'ISATS Portal'}</h2>
              {branding?.supportEmail && (
                <p className="text-caption text-slate-400">Support: {branding.supportEmail}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-blue-700 font-bold bg-blue-50 px-3 py-1.5 rounded-full border border-blue-200 shrink-0">
            <ShieldCheck className="h-4 w-4" /> Official Estimate / Quote
          </div>
        </div>

        {/* Feedback Alert */}
        {feedbackMsg && (
          <div className={`p-4 rounded-2xl border text-xs font-bold flex items-center gap-3 ${
            feedbackMsg.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}>
            {feedbackMsg.type === 'success'
              ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
              : <XCircle className="h-5 w-5 text-rose-600 shrink-0" />}
            <span>{feedbackMsg.text}</span>
          </div>
        )}

        {/* Quote Summary Card */}
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs space-y-6">
          <div className="flex justify-between items-start pb-4 border-b border-slate-100">
            <div>
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">Official Quote</span>
              <h1 className="text-xl font-black text-slate-900 font-mono">#{quoteData.quoteNumber}</h1>
              {quoteData.applicantName && (
                <p className="text-xs font-bold text-slate-800 mt-1">{quoteData.applicantName}</p>
              )}
            </div>
            <div className="text-right space-y-1">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${badge.cls}`}>
                {currentStatus === 'Accepted' ? <Check className="h-3 w-3" /> : currentStatus === 'Rejected' ? <X className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                {badge.label}
              </span>
              {quoteData.programName && (
                <p className="text-caption text-slate-500 block">{quoteData.programName}{quoteData.term ? ` (${quoteData.term})` : ''}</p>
              )}
            </div>
          </div>

          {/* Meta row */}
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">Issue Date</span>
              <span className="font-bold text-slate-800">{quoteData.issueDate || 'N/A'}</span>
            </div>
            <div>
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider block">Expiration Date</span>
              <span className="font-bold text-slate-800">{quoteData.expirationDate || 'N/A'}</span>
            </div>
          </div>

          {/* Items Table */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-black uppercase text-slate-400 tracking-wider">
                  <th className="p-3">Item / Description</th>
                  <th className="p-3 text-center">Qty</th>
                  <th className="p-3 text-right">Rate</th>
                  <th className="p-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {Array.isArray(quoteData.items) && quoteData.items.map((item: any, idx: number) => (
                  <tr key={idx}>
                    <td className="p-3">
                      <div className="font-bold text-slate-800">{item.name}</div>
                      {item.description && <div className="text-[11px] text-slate-400 mt-0.5">{item.description}</div>}
                    </td>
                    <td className="p-3 text-center font-bold">{item.quantity || 1}</td>
                    <td className="p-3 text-right font-mono font-bold">{formatMoney(item.rate, quoteData.currency)}</td>
                    <td className="p-3 text-right font-mono font-bold">{formatMoney(item.amount, quoteData.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Financial Breakdown */}
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 space-y-2 text-xs">
            <div className="flex justify-between text-slate-600">
              <span>Subtotal:</span>
              <span className="font-mono font-bold">{formatMoney(quoteData.subtotal, quoteData.currency)}</span>
            </div>
            {Number(quoteData.totalDiscount || 0) > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>Discounts:</span>
                <span className="font-mono font-bold">-{formatMoney(quoteData.totalDiscount, quoteData.currency)}</span>
              </div>
            )}
            {Number(quoteData.adjustment || 0) !== 0 && (
              <div className="flex justify-between text-indigo-700">
                <span>Adjustment:</span>
                <span className="font-mono font-bold">{formatMoney(quoteData.adjustment, quoteData.currency)}</span>
              </div>
            )}
            <hr className="border-slate-200 my-1" />
            <div className="flex justify-between text-sm font-black text-slate-900">
              <span>Total Estimated Investment:</span>
              <span className="font-mono text-blue-600">{formatMoney(quoteData.total, quoteData.currency)}</span>
            </div>
          </div>

          {quoteData.notes && (
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 text-xs text-slate-600 space-y-1">
              <span className="font-bold text-slate-700 block">Terms & Notes:</span>
              <p className="leading-relaxed whitespace-pre-line">{quoteData.notes}</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleDownloadPdf}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-all cursor-pointer"
          >
            <Download className="h-4 w-4" /> Download Vector PDF
          </button>
        </div>

        {/* Action / Decision Card */}
        {currentStatus === 'Accepted' || currentStatus === 'Rejected' ? (
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs text-center space-y-3">
            <div className={`h-14 w-14 rounded-full flex items-center justify-center mx-auto ${
              currentStatus === 'Accepted' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'
            }`}>
              {currentStatus === 'Accepted' ? <Check className="h-7 w-7" /> : <X className="h-7 w-7" />}
            </div>
            <h3 className="text-base font-extrabold text-slate-900">
              Quote {currentStatus === 'Accepted' ? 'Accepted' : 'Declined'}
            </h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
              {currentStatus === 'Accepted'
                ? 'Thank you — your acceptance has been recorded. Our administrative team will issue your final invoice and program schedule shortly.'
                : 'This quote offer has been declined. If you change your mind or have questions, please contact our support team.'}
            </p>
          </div>
        ) : (
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-blue-600" />
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-800">Accept or Decline Offer</h3>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Review the line items above. To accept this quote and program pricing, click <strong className="text-slate-700">Accept Quote</strong> below. No login or account is required.
            </p>

            <textarea
              rows={2}
              placeholder="Optional notes or questions regarding this quote..."
              value={decisionNotes}
              onChange={e => setDecisionNotes(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-800 outline-none focus:border-blue-600 focus:bg-white resize-none"
            />

            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <button
                type="button"
                disabled={submitting}
                onClick={() => setConfirmDecision('Accepted')}
                className="flex-1 py-3 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:bg-slate-300 text-white font-extrabold rounded-xl text-xs transition-all shadow-lg shadow-success/20 flex items-center justify-center gap-2 cursor-pointer disabled:cursor-not-allowed"
              >
                {submitting ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {submitting ? 'Submitting...' : 'Accept Quote & Offer'}
              </button>

              <button
                type="button"
                disabled={submitting}
                onClick={() => setConfirmDecision('Rejected')}
                className="px-6 py-3 bg-white border border-slate-300 hover:bg-slate-50 text-slate-600 font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <XCircle className="h-4 w-4" /> Decline Quote
              </button>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmDecision && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-xs" onClick={() => setConfirmDecision(null)} />
            <div className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full space-y-4 border border-slate-200">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-3">
                <div className={`p-2.5 rounded-xl ${confirmDecision === 'Accepted' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                  {confirmDecision === 'Accepted' ? <CheckCircle2 className="h-6 w-6" /> : <XCircle className="h-6 w-6" />}
                </div>
                <div>
                  <h3 className="text-sm font-black text-slate-900">
                    Confirm {confirmDecision === 'Accepted' ? 'Quote Acceptance' : 'Decline Quote'}
                  </h3>
                  <p className="text-caption text-slate-400">Quote #{quoteData.quoteNumber}</p>
                </div>
              </div>

              <p className="text-xs text-slate-600 leading-relaxed">
                {confirmDecision === 'Accepted'
                  ? `Are you sure you want to accept Quote #${quoteData.quoteNumber} for ${formatMoney(quoteData.total, quoteData.currency)}? Once accepted, your agreement will be recorded and processing will begin.`
                  : `Are you sure you want to decline Quote #${quoteData.quoteNumber}? This will mark the quote offer as declined.`}
              </p>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setConfirmDecision(null)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 font-bold text-xs rounded-xl hover:bg-slate-50 transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => {
                    const decision = confirmDecision;
                    setConfirmDecision(null);
                    handleDecision(decision);
                  }}
                  className={`px-5 py-2 text-white font-extrabold text-xs rounded-xl shadow-md transition cursor-pointer flex items-center gap-1.5 ${
                    confirmDecision === 'Accepted' ? 'bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)]' : 'bg-rose-600 hover:bg-rose-700'
                  }`}
                >
                  {submitting && <Loader className="h-3.5 w-3.5 animate-spin" />}
                  Confirm {confirmDecision === 'Accepted' ? 'Acceptance' : 'Decline'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Wave 4 (D-05): disclaimer + refund/terms links on the quote */}
        <div className="space-y-2">
          <NonAttorneyDisclaimer />
          <p className="text-center text-[11px] text-slate-400">
            See the{' '}
            <a href="/terms" target="_blank" rel="noreferrer" className="underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/refund-policy" target="_blank" rel="noreferrer" className="underline">Refund Policy</a>.
          </p>
        </div>

        <p className="text-center text-[11px] text-slate-400 flex items-center justify-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5" /> Secure quote link provided by {branding?.workspaceName || 'ISATS Portal'}
        </p>
      </div>
    </div>
  );
}

function formatMoney(value: any, currency?: string): string {
  const symbol = currency === 'EUR' ? '€' : currency === 'GBP' ? '£' : '$';
  return `${symbol}${Number(value || 0).toFixed(2)}${currency && currency !== 'USD' && currency !== 'EUR' && currency !== 'GBP' ? ` ${currency}` : ''}`;
}
